import { useState } from "react";
import { uploadImage } from "../../utils/uploadImage";

interface EventImageUploadsProps {
  featuredImageUrl: string;
  galleryImageUrls: string[];
  onFeaturedChange: (url: string) => void;
  onGalleryChange: (index: number, url: string) => void;
}

function EventImageUploads({
  featuredImageUrl,
  galleryImageUrls,
  onFeaturedChange,
  onGalleryChange,
}: EventImageUploadsProps) {
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadingSlot, setUploadingSlot] = useState<string | null>(null);

  const handleUpload = async (file: File, slot: string, onChange: (url: string) => void) => {
    setUploadError(null);
    setUploadingSlot(slot);
    try {
      const url = await uploadImage(file);
      onChange(url);
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : "Upload failed");
    } finally {
      setUploadingSlot(null);
    }
  };

  return (
    <div className="admin-image-uploads">
      <h3 className="h2-text" style={{ marginBottom: 16 }}>
        Event photos
      </h3>
      <p className="body-text" style={{ marginBottom: 16 }}>
        Upload 1 featured image and up to 4 gallery images.
      </p>

      <div className="admin-image-uploads__grid">
        <div className="admin-image-upload">
          <label className="body-text" htmlFor="featured-image">
            Featured image
          </label>
          {featuredImageUrl && (
            <img src={featuredImageUrl} alt="Featured preview" className="admin-image-upload__preview" />
          )}
          <input
            id="featured-image"
            type="file"
            accept="image/*"
            disabled={uploadingSlot === "featured"}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleUpload(file, "featured", onFeaturedChange);
              e.target.value = "";
            }}
          />
          {featuredImageUrl && (
            <button
              type="button"
              className="admin-btn admin-btn--danger"
              onClick={() => onFeaturedChange("")}
            >
              Remove
            </button>
          )}
        </div>

        {Array.from({ length: 4 }, (_, index) => (
          <div key={index} className="admin-image-upload">
            <label className="body-text" htmlFor={`gallery-image-${index}`}>
              Gallery image {index + 1}
            </label>
            {galleryImageUrls[index] && (
              <img
                src={galleryImageUrls[index]}
                alt={`Gallery preview ${index + 1}`}
                className="admin-image-upload__preview"
              />
            )}
            <input
              id={`gallery-image-${index}`}
              type="file"
              accept="image/*"
              disabled={uploadingSlot === `gallery-${index}`}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleUpload(file, `gallery-${index}`, (url) => onGalleryChange(index, url));
                e.target.value = "";
              }}
            />
            {galleryImageUrls[index] && (
              <button
                type="button"
                className="admin-btn admin-btn--danger"
                onClick={() => onGalleryChange(index, "")}
              >
                Remove
              </button>
            )}
          </div>
        ))}
      </div>

      {uploadingSlot && <p className="body-text">Uploading {uploadingSlot.replace("-", " ")}...</p>}
      {uploadError && <p className="admin-error">{uploadError}</p>}
    </div>
  );
}

export default EventImageUploads;
